import { useState } from "react";
import { Link } from "react-router-dom";
import { X, Lightning, Trophy, Crown, ArrowRight } from "@phosphor-icons/react";

const STORAGE_KEY = "nk-welcome-tour-dismissed-v1";

/**
 * One-time welcome card for new players. Three steps, dismissible.
 * Self-storing — no backend dependency. Hides forever once dismissed.
 */
export default function WelcomeTour({ username }) {
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1"
  );
  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="welcome-tour"
      className="nk-card nk-card-lift p-5 sm:p-6 relative overflow-hidden border-nk-gold/40 bg-gradient-to-br from-nk-green/30 to-transparent"
    >
      <button
        onClick={dismiss}
        data-testid="welcome-tour-dismiss"
        aria-label="Dismiss welcome"
        className="absolute top-3 right-3 p-1.5 text-white/40 hover:text-white transition-colors"
      >
        <X size={14} weight="bold" />
      </button>

      <p className="eyebrow text-nk-gold mb-2">New here</p>
      <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-white">
        Welcome{username ? <>, <span className="italic font-light text-nk-gold">{username}</span></> : ""}.
      </h2>
      <p className="text-sm text-white/70 mt-2 max-w-xl">
        NKognito is a 30-day knowledge arena. Three things matter &mdash; pick a mode, climb your country&apos;s table, hold on until day 30.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
        <Step n="1" Icon={Lightning} title="Play a mode" body="Daily, Ladder, Duel or Survival. Always-on." />
        <Step n="2" Icon={Trophy} title="Earn Season Score" body="Every game adds to your country's count." />
        <Step n="3" Icon={Crown} title="Survive to day 30" body="Top of your country = champion + real prize." />
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <Link
          to="/daily"
          data-testid="welcome-tour-start"
          onClick={dismiss}
          className="nk-btn nk-btn-gold"
        >
          Play your first round <ArrowRight size={14} weight="bold" />
        </Link>
        <button
          onClick={dismiss}
          data-testid="welcome-tour-skip"
          className="nk-btn nk-btn-ghost text-xs"
        >
          I&apos;ll find my way
        </button>
      </div>
    </div>
  );
}

function Step({ n, Icon, title, body }) {
  return (
    <div className="border border-white/8 rounded-sm p-3 bg-black/20">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-5 h-5 rounded-full bg-nk-gold text-nk-charcoal font-mono text-[10px] font-bold flex items-center justify-center">
          {n}
        </span>
        <Icon size={14} weight="duotone" className="text-nk-gold" />
        <p className="text-sm font-medium text-white">{title}</p>
      </div>
      <p className="text-[11px] text-white/55 leading-snug">{body}</p>
    </div>
  );
}
